import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Plus, Trash2, X, Target, FileText, Printer, FileDown, Pencil } from "lucide-react";
import { toast } from "sonner";
import UnitScopeTabs from "../../components/UnitScopeTabs";
import MetaProgressBar from "../../components/MetaProgressBar";
import { toCSV, downloadCSV } from "../../lib/csv";

const money = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;
const emptyForm = {
  unidade_id: "",
  titulo: "",
  periodo_inicio: "",
  periodo_fim: "",
  meta_faturamento: 0,
  metas_vendedores: [], // [{vendedor_id, qtd_itens}]
  metas_itens_alvo: [], // [{produto_id, qtd}]
};

export default function MetasMaster() {
  const [unidades, setUnidades] = useState([]);
  const [scope, setScope] = useState("global");
  const [metas, setMetas] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [estoqueAll, setEstoqueAll] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [progresso, setProgresso] = useState(null); // {meta, show}

  useEffect(() => {
    api.get("/unidades").then((r) => setUnidades(r.data));
    api.get("/vendedores").then((r) => setVendedores(r.data));
    api.get("/produtos").then((r) => setProdutos(r.data));
    api.get("/estoque").then((r) => setEstoqueAll(r.data));
  }, []);

  // Produtos que têm estoque cadastrado na unidade selecionada no formulário
  const produtosDaUnidade = form.unidade_id
    ? (() => {
        const pids = new Set(
          estoqueAll
            .filter((e) => e.unidade_id === form.unidade_id)
            .map((e) => e.produto_id),
        );
        return produtos.filter((p) => pids.has(p.id));
      })()
    : [];

  const load = () => {
    const params = scope === "global" ? {} : { unidade_id: scope };
    api.get("/metas", { params }).then((r) => setMetas(r.data));
  };
  useEffect(() => {
    load();
  }, [scope]); // eslint-disable-line

  const openNew = () => {
    setForm({ ...emptyForm, unidade_id: scope !== "global" ? scope : unidades[0]?.id || "" });
    setModal({ mode: "new" });
  };

  const openEdit = (m) => {
    setForm({
      unidade_id: m.unidade_id,
      titulo: m.titulo,
      periodo_inicio: m.periodo_inicio,
      periodo_fim: m.periodo_fim,
      meta_faturamento: Number(m.meta_faturamento) || 0,
      metas_vendedores: (m.metas_vendedores || []).map((v) => ({
        vendedor_id: v.vendedor_id,
        qtd_itens: Number(v.qtd_itens) || 0,
      })),
      metas_itens_alvo: (m.metas_itens_alvo || []).map((i) => ({
        produto_id: i.produto_id,
        qtd: Number(i.qtd) || 0,
      })),
    });
    setModal({ mode: "edit", metaId: m.id, metaTitulo: m.titulo, ativa: m.ativa });
  };

  const salvar = async () => {
    if (!form.titulo || !form.unidade_id || !form.periodo_inicio || !form.periodo_fim) {
      toast.error("Preencha título, unidade e período");
      return;
    }
    try {
      if (modal.mode === "edit") {
        await api.put(`/metas/${modal.metaId}`, form);
        toast.success("Meta atualizada");
      } else {
        await api.post("/metas", form);
        toast.success("Meta criada (anteriores desativadas)");
      }
      setModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar meta");
    }
  };

  const excluir = async (m) => {
    if (!window.confirm(`Excluir meta "${m.titulo}"?`)) return;
    await api.delete(`/metas/${m.id}`);
    toast.success("Meta excluída");
    load();
  };

  const verProgresso = async (m) => {
    const r = await api.get(`/metas/${m.id}`);
    setProgresso({ ...r.data, unidade_nome: unidades.find((u) => u.id === r.data.unidade_id)?.nome });
  };

  const addVendedorMeta = () => {
    const vs = vendedores.filter((v) => v.unidade_id === form.unidade_id);
    if (vs.length === 0) return toast.error("Selecione a unidade antes");
    setForm({
      ...form,
      metas_vendedores: [...form.metas_vendedores, { vendedor_id: vs[0].id, qtd_itens: 0 }],
    });
  };
  const addItemAlvo = () => {
    if (!form.unidade_id) {
      toast.error("Selecione a unidade antes");
      return;
    }
    if (produtosDaUnidade.length === 0) {
      toast.error("Esta unidade não tem produtos cadastrados no estoque");
      return;
    }
    setForm({
      ...form,
      metas_itens_alvo: [
        ...form.metas_itens_alvo,
        { produto_id: produtosDaUnidade[0].id, qtd: 0 },
      ],
    });
  };

  const exportCSV = (meta) => {
    const rows = [];
    rows.push({ secao: "Faturamento", nome: "Meta", alvo: meta.meta_faturamento, atual: meta.faturamento_atual, pct: meta.percentual_faturamento });
    meta.metas_vendedores_progresso?.forEach((v) =>
      rows.push({ secao: "Vendedor", nome: v.vendedor_nome, alvo: v.qtd_alvo, atual: v.qtd_atual, pct: v.percentual })
    );
    meta.metas_itens_alvo_progresso?.forEach((i) =>
      rows.push({ secao: "Item-alvo", nome: i.produto_nome, alvo: i.qtd_alvo, atual: i.qtd_atual, pct: i.percentual })
    );
    const csv = toCSV(rows, [
      { label: "Secao", get: (r) => r.secao },
      { label: "Nome", get: (r) => r.nome },
      { label: "Alvo", get: (r) => r.alvo },
      { label: "Atual", get: (r) => r.atual },
      { label: "% Alcancado", get: (r) => Number(r.pct).toFixed(2).replace(".", ",") },
    ]);
    downloadCSV(`meta_${meta.titulo.replace(/\W+/g, "_")}.csv`, csv);
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-neutral-500">
            Definir & acompanhar
          </p>
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">
            Metas
          </h2>
        </div>
        <button
          data-testid="meta-nova-btn"
          onClick={openNew}
          className="bg-neutral-950 text-white h-11 px-4 font-display font-bold uppercase text-xs tracking-widest flex items-center gap-2 cta-brutal"
        >
          <Plus className="w-4 h-4" /> Nova meta
        </button>
      </div>

      <UnitScopeTabs unidades={unidades} value={scope} onChange={setScope} />

      <div className="bg-white border-2 border-neutral-900 overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-neutral-300 bg-neutral-50">
              <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest">Título</th>
              <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest">Unidade</th>
              <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest hidden md:table-cell">Período</th>
              <th className="text-right p-3 font-mono text-[10px] uppercase tracking-widest">Meta Fat.</th>
              <th className="text-center p-3 font-mono text-[10px] uppercase tracking-widest">Ativa</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {metas.map((m) => (
              <tr key={m.id} data-testid={`meta-row-${m.id}`} className="border-b border-neutral-100 hover:bg-neutral-50">
                <td className="p-3 font-display font-bold">{m.titulo}</td>
                <td className="p-3 text-sm">{unidades.find((u) => u.id === m.unidade_id)?.nome}</td>
                <td className="p-3 font-mono text-xs hidden md:table-cell">
                  {new Date(m.periodo_inicio + "T00:00").toLocaleDateString("pt-BR")} → {new Date(m.periodo_fim + "T00:00").toLocaleDateString("pt-BR")}
                </td>
                <td className="p-3 text-right font-mono font-bold">{money(m.meta_faturamento)}</td>
                <td className="p-3 text-center">
                  {m.ativa ? (
                    <span className="inline-block bg-green-600 text-white px-2 py-0.5 font-mono text-[10px] uppercase">Ativa</span>
                  ) : (
                    <span className="inline-block bg-neutral-200 text-neutral-700 px-2 py-0.5 font-mono text-[10px] uppercase">Inativa</span>
                  )}
                </td>
                <td className="p-3 flex gap-1 justify-end">
                  <button
                    data-testid={`meta-progresso-${m.id}`}
                    onClick={() => verProgresso(m)}
                    className="p-2 border border-neutral-900 hover:bg-neutral-900 hover:text-white"
                    title="Ver progresso"
                  >
                    <FileText className="w-4 h-4" />
                  </button>
                  <button
                    data-testid={`meta-editar-${m.id}`}
                    onClick={() => openEdit(m)}
                    className="p-2 border border-neutral-900 hover:bg-neutral-900 hover:text-white"
                    title="Editar meta"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    data-testid={`meta-excluir-${m.id}`}
                    onClick={() => excluir(m)}
                    className="p-2 border border-neutral-900 hover:bg-red-600 hover:text-white hover:border-red-600"
                    title="Excluir meta"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            {metas.length === 0 && (
              <tr>
                <td colSpan={6} className="p-8 text-center text-sm text-neutral-500">
                  Nenhuma meta definida
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Nova Meta */}
      {modal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white border-4 border-neutral-900 w-full max-w-2xl max-h-[92vh] overflow-auto">
            <div className="flex items-center justify-between p-4 border-b-2 border-neutral-900">
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5" />
                <h3 className="font-display font-black text-xl tracking-tight">
                  {modal.mode === "edit" ? "Editar Meta" : "Nova Meta"}
                </h3>
              </div>
              <button onClick={() => setModal(null)} className="p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <L label="Unidade">
                  <select
                    data-testid="meta-form-unidade"
                    value={form.unidade_id}
                    disabled={modal.mode === "edit"}
                    onChange={(e) => setForm({ ...form, unidade_id: e.target.value, metas_vendedores: [], metas_itens_alvo: [] })}
                    className="mt-1 w-full h-11 px-3 border-2 border-neutral-900 bg-white font-mono text-sm disabled:bg-neutral-100 disabled:cursor-not-allowed"
                  >
                    {unidades.map((u) => (
                      <option key={u.id} value={u.id}>{u.nome}</option>
                    ))}
                  </select>
                  {modal.mode === "edit" && (
                    <span className="font-mono text-[10px] text-neutral-500">
                      Unidade não pode ser alterada em edição.
                    </span>
                  )}
                </L>
                <L label="Título">
                  <input
                    data-testid="meta-form-titulo"
                    value={form.titulo}
                    onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                    className="mt-1 w-full h-11 px-3 border-2 border-neutral-900 font-mono text-sm"
                    placeholder="Ex: Abril 2026"
                  />
                </L>
                <L label="Início">
                  <input
                    data-testid="meta-form-inicio"
                    type="date"
                    value={form.periodo_inicio}
                    onChange={(e) => setForm({ ...form, periodo_inicio: e.target.value })}
                    className="mt-1 w-full h-11 px-3 border-2 border-neutral-900 font-mono text-sm"
                  />
                </L>
                <L label="Fim">
                  <input
                    data-testid="meta-form-fim"
                    type="date"
                    value={form.periodo_fim}
                    onChange={(e) => setForm({ ...form, periodo_fim: e.target.value })}
                    className="mt-1 w-full h-11 px-3 border-2 border-neutral-900 font-mono text-sm"
                  />
                </L>
                <L label="Meta Faturamento (R$)">
                  <input
                    data-testid="meta-form-fat"
                    type="number"
                    step="0.01"
                    value={form.meta_faturamento}
                    onChange={(e) => setForm({ ...form, meta_faturamento: Number(e.target.value) })}
                    className="mt-1 w-full h-11 px-3 border-2 border-neutral-900 font-mono"
                  />
                </L>
              </div>

              <div className="border-t-2 border-neutral-900 pt-3">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-display font-bold uppercase text-sm">Metas de itens por vendedor</h4>
                  <button data-testid="meta-add-vendedor" onClick={addVendedorMeta} className="text-xs font-mono uppercase tracking-widest px-2 py-1 border border-neutral-900 hover:bg-neutral-900 hover:text-white">+ Vendedor</button>
                </div>
                {form.metas_vendedores.length === 0 && <p className="text-xs text-neutral-500">Nenhum vendedor com meta individual</p>}
                {form.metas_vendedores.map((mv, idx) => {
                  const vs = vendedores.filter((v) => v.unidade_id === form.unidade_id);
                  return (
                    <div key={idx} className="flex gap-2 mb-2">
                      <select
                        value={mv.vendedor_id}
                        onChange={(e) => {
                          const n = [...form.metas_vendedores];
                          n[idx].vendedor_id = e.target.value;
                          setForm({ ...form, metas_vendedores: n });
                        }}
                        className="flex-1 h-10 px-2 border-2 border-neutral-900 font-mono text-sm bg-white"
                      >
                        {vs.map((v) => <option key={v.id} value={v.id}>{v.nome}</option>)}
                      </select>
                      <input
                        type="number"
                        value={mv.qtd_itens}
                        onChange={(e) => {
                          const n = [...form.metas_vendedores];
                          n[idx].qtd_itens = Number(e.target.value);
                          setForm({ ...form, metas_vendedores: n });
                        }}
                        placeholder="Qtd itens"
                        className="w-28 h-10 px-2 border-2 border-neutral-900 font-mono text-sm"
                      />
                      <button
                        onClick={() => setForm({ ...form, metas_vendedores: form.metas_vendedores.filter((_, i) => i !== idx) })}
                        className="p-2 border-2 border-neutral-900 hover:bg-red-600 hover:text-white"
                      ><Trash2 className="w-4 h-4" /></button>
                    </div>
                  );
                })}
              </div>

              <div className="border-t-2 border-neutral-900 pt-3">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-display font-bold uppercase text-sm">Itens-alvo (unidade)</h4>
                  <button data-testid="meta-add-item" onClick={addItemAlvo} className="text-xs font-mono uppercase tracking-widest px-2 py-1 border border-neutral-900 hover:bg-neutral-900 hover:text-white">+ Produto</button>
                </div>
                {form.metas_itens_alvo.length === 0 && <p className="text-xs text-neutral-500">Nenhum item-alvo definido</p>}
                {form.metas_itens_alvo.map((mi, idx) => (
                  <div key={idx} className="flex gap-2 mb-2">
                    <select
                      value={mi.produto_id}
                      onChange={(e) => {
                        const n = [...form.metas_itens_alvo];
                        n[idx].produto_id = e.target.value;
                        setForm({ ...form, metas_itens_alvo: n });
                      }}
                      className="flex-1 h-10 px-2 border-2 border-neutral-900 font-mono text-sm bg-white"
                    >
                      {produtosDaUnidade.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
                    </select>
                    <input
                      type="number"
                      value={mi.qtd}
                      onChange={(e) => {
                        const n = [...form.metas_itens_alvo];
                        n[idx].qtd = Number(e.target.value);
                        setForm({ ...form, metas_itens_alvo: n });
                      }}
                      placeholder="Qtd alvo"
                      className="w-28 h-10 px-2 border-2 border-neutral-900 font-mono text-sm"
                    />
                    <button
                      onClick={() => setForm({ ...form, metas_itens_alvo: form.metas_itens_alvo.filter((_, i) => i !== idx) })}
                      className="p-2 border-2 border-neutral-900 hover:bg-red-600 hover:text-white"
                    ><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-4 border-t-2 border-neutral-900 flex justify-end gap-2">
              <button onClick={() => setModal(null)} className="h-11 px-4 border-2 border-neutral-900 bg-white font-display font-bold uppercase text-xs tracking-widest">Cancelar</button>
              <button data-testid="meta-form-salvar" onClick={salvar} className="bg-neutral-950 text-white h-11 px-6 font-display font-bold uppercase text-xs tracking-widest cta-brutal">{modal.mode === "edit" ? "Salvar alterações" : "Criar meta"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Progresso/Relatório */}
      {progresso && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white border-4 border-neutral-900 w-full max-w-2xl max-h-[92vh] overflow-auto">
            <div className="flex items-center justify-between p-4 border-b-2 border-neutral-900 print:hidden">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                  {progresso.unidade_nome}
                </p>
                <h3 className="font-display font-black text-xl">{progresso.titulo}</h3>
                <p className="font-mono text-xs text-neutral-600">
                  {new Date(progresso.periodo_inicio + "T00:00").toLocaleDateString("pt-BR")} → {new Date(progresso.periodo_fim + "T00:00").toLocaleDateString("pt-BR")}
                </p>
              </div>
              <div className="flex gap-2">
                <button data-testid="meta-csv" onClick={() => exportCSV(progresso)} className="bg-neutral-950 text-white h-10 px-4 font-display font-bold uppercase text-xs tracking-widest flex items-center gap-2">
                  <FileDown className="w-4 h-4" /> CSV
                </button>
                <button data-testid="meta-imprimir" onClick={() => window.print()} className="bg-yellow-400 text-neutral-950 h-10 px-4 font-display font-bold uppercase text-xs tracking-widest flex items-center gap-2">
                  <Printer className="w-4 h-4" /> Térmico
                </button>
                <button onClick={() => setProgresso(null)} className="p-2 border-2 border-neutral-900 hover:bg-neutral-900 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="p-4 space-y-4 print:hidden">
              <MetaProgressBar label="Faturamento" current={progresso.faturamento_atual} target={progresso.meta_faturamento} color={progresso.bate_meta_tendencia ? "bg-green-600" : "bg-red-600"} />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs font-mono">
                <Small label="Atual" value={money(progresso.faturamento_atual)} />
                <Small label="Falta" value={money(progresso.falta)} />
                <Small label="Diário necessário" value={money(progresso.faturamento_diario_necessario)} />
                <Small label="Projeção" value={money(progresso.projecao_final)} />
              </div>
              {progresso.metas_vendedores_progresso?.length > 0 && (
                <div>
                  <h4 className="font-display font-bold uppercase text-sm mb-2">Vendedores</h4>
                  <div className="space-y-2">
                    {progresso.metas_vendedores_progresso.map((v) => (
                      <MetaProgressBar key={v.vendedor_id} label={v.vendedor_nome} current={v.qtd_atual} target={v.qtd_alvo} unit=" itens" />
                    ))}
                  </div>
                </div>
              )}
              {progresso.metas_itens_alvo_progresso?.length > 0 && (
                <div>
                  <h4 className="font-display font-bold uppercase text-sm mb-2">Itens-alvo</h4>
                  <div className="space-y-2">
                    {progresso.metas_itens_alvo_progresso.map((it) => (
                      <MetaProgressBar key={it.produto_id} label={it.produto_nome} current={it.qtd_atual} target={it.qtd_alvo} unit=" un" />
                    ))}
                  </div>
                </div>
              )}
            </div>
            {/* Versão para impressão térmica */}
            <div id="thermal-receipt" className="hidden print:block thermal-receipt mx-auto">
              <div className="text-center font-bold text-sm uppercase">META · {progresso.titulo}</div>
              <div className="text-center text-[10px]">{progresso.unidade_nome}</div>
              <hr />
              <div className="text-[11px]">
                PERÍODO: {new Date(progresso.periodo_inicio + "T00:00").toLocaleDateString("pt-BR")} → {new Date(progresso.periodo_fim + "T00:00").toLocaleDateString("pt-BR")}
              </div>
              <hr />
              <div className="text-[11px]">
                <div className="font-bold">FATURAMENTO</div>
                <div className="flex justify-between"><span>Atual</span><span>{money(progresso.faturamento_atual)}</span></div>
                <div className="flex justify-between"><span>Meta</span><span>{money(progresso.meta_faturamento)}</span></div>
                <div className="flex justify-between"><span>% Alcançado</span><span>{progresso.percentual_faturamento}%</span></div>
                <div className="flex justify-between"><span>Falta</span><span>{money(progresso.falta)}</span></div>
                <div className="flex justify-between"><span>Diário necessário</span><span>{money(progresso.faturamento_diario_necessario)}</span></div>
                <div className="flex justify-between font-bold"><span>Tendência</span><span>{progresso.bate_meta_tendencia ? "NO RITMO" : "ABAIXO"}</span></div>
              </div>
              {progresso.metas_vendedores_progresso?.length > 0 && (<><hr /><div className="text-[11px]">
                <div className="font-bold">VENDEDORES</div>
                {progresso.metas_vendedores_progresso.map((v) => (
                  <div key={v.vendedor_id} className="flex justify-between"><span>{v.vendedor_nome}</span><span>{v.qtd_atual}/{v.qtd_alvo} ({v.percentual}%)</span></div>
                ))}
              </div></>)}
              {progresso.metas_itens_alvo_progresso?.length > 0 && (<><hr /><div className="text-[11px]">
                <div className="font-bold">ITENS-ALVO</div>
                {progresso.metas_itens_alvo_progresso.map((it) => (
                  <div key={it.produto_id} className="flex justify-between"><span>{it.produto_nome}</span><span>{it.qtd_atual}/{it.qtd_alvo} ({it.percentual}%)</span></div>
                ))}
              </div></>)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const L = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{label}</span>
    {children}
  </label>
);
const Small = ({ label, value }) => (
  <div className="bg-neutral-50 border border-neutral-300 p-2">
    <div className="text-[9px] uppercase tracking-widest text-neutral-500">{label}</div>
    <div className="font-bold">{value}</div>
  </div>
);
